import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationActions } from '@next/core-lib/notification';
import { Store } from '@ngrx/store';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';

import { CustomerService } from '../../../customer/services/customer.service';
import { extractValidationErrors } from '../../../shared/helper.functions';
import { Customer, CustomerLocation } from '../../../shared/models/shared.model';
import { EmployeeRelationship } from '../../models/employee.model';
import { EmployeeService } from '../../services/employee.service';

type EmployeeRelationLocation = CustomerLocation & {
  hasRelation: boolean;
};

export function getLocationIdsAfterToggle(
  locations: EmployeeRelationLocation[] | undefined,
  locationId: number,
  checked: boolean,
) {
  return (locations ?? [])
    .filter((location) => (location.id === locationId ? checked : location.hasRelation))
    .map((location) => location.id);
}

export function hasShipToRelationship(relationships: EmployeeRelationship[], locationId: number) {
  return relationships.some(
    (relationship) =>
      relationship.relatedExternalId === locationId &&
      (relationship.relatedType === 'ShipTo' || relationship.relatedType === 1),
  );
}

export function hasCustomerRelationship(relationships: EmployeeRelationship[], customerId: number) {
  return relationships.some(
    (relationship) =>
      relationship.relatedExternalId === customerId &&
      (relationship.relatedType === 'Customer' || relationship.relatedType === 0),
  );
}

export function getQueryParamNumber(value: string | null) {
  if (value === null) {
    return null;
  }

  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? null : numberValue;
}

@Component({
  selector: 'app-employee-relation-create',
  templateUrl: './employee-relation-create.component.html',
  styleUrls: ['./employee-relation-create.component.scss'],
  standalone: false,
})
export class EmployeeRelationCreateComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly employeeService = inject(EmployeeService);
  private readonly customerService = inject(CustomerService);

  readonly isLoadingSubject = new BehaviorSubject(false);
  readonly isLoading$ = this.isLoadingSubject.asObservable();
  readonly customerSearchSubject = new BehaviorSubject('');
  readonly customers$ = this.customerSearchSubject.pipe(
    distinctUntilChanged(),
    tap(() => this.isLoadingSubject.next(true)),
    switchMap((query) =>
      this.employeeService.getCustomers(query).pipe(catchError(() => of([] as Customer[]))),
    ),
    tap(() => this.isLoadingSubject.next(false)),
  );

  readonly selectedCustomer = signal<Customer | null>(null);
  employeeId: number | null = null;
  readonly employeeRelationships = signal<EmployeeRelationship[]>([]);
  readonly locations = signal<EmployeeRelationLocation[] | undefined>(undefined);
  readonly hasCustomerRelation = signal(false);
  readonly saving = signal(false);

  ngOnInit() {
    this.employeeId = getQueryParamNumber(
      this.route.parent?.parent?.snapshot.paramMap.get('employeeId') ?? null,
    );
    if (this.employeeId === null) {
      return;
    }

    this.employeeService.getEmployee(this.employeeId).subscribe((employee) => {
      this.employeeRelationships.set(employee.relationships ?? []);

      const customerId = getQueryParamNumber(this.route.snapshot.queryParamMap.get('customerId'));
      if (customerId !== null) {
        this.customerService.getCustomerById(customerId).subscribe((customer) => {
          this.selectedCustomer.set(customer);
          this.loadLocations(customer.id);
        });
      }
    });
  }

  backToRelations() {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  searchValueChanged(searchValue: string) {
    if (typeof searchValue !== 'string') {
      return;
    }

    this.selectedCustomer.set(null);
    this.locations.set(undefined);
    this.hasCustomerRelation.set(false);
    this.customerSearchSubject.next(searchValue ?? '');
  }

  selectCustomer(customer: unknown) {
    this.selectedCustomer.set(customer as Customer);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { customerId: this.selectedCustomer()?.id },
      queryParamsHandling: 'merge',
    });
    this.loadLocations(this.selectedCustomer()!.id);
  }

  clickRelationForLocation(event: MouseEvent, location: EmployeeRelationLocation) {
    event.preventDefault();
    this.upsertLocations(
      getLocationIdsAfterToggle(this.locations(), location.id, !location.hasRelation),
    );
  }

  toggleRelationForLocation(event: MatSlideToggleChange, location: EmployeeRelationLocation) {
    this.upsertLocations(getLocationIdsAfterToggle(this.locations(), location.id, event.checked));
  }

  toggleAllLocations(event: MatSlideToggleChange) {
    this.upsertLocations(
      this.locations()
        ?.filter((it) => it.hasRelation)
        .map((it) => it.id) ?? [],
      event.checked,
    );
  }

  private loadLocations(customerId: number) {
    this.locations.set(undefined);
    this.hasCustomerRelation.set(hasCustomerRelationship(this.employeeRelationships(), customerId));
    this.customerService.getLocations(customerId).subscribe((locations) => {
      this.locations.set(
        locations.map((location) => ({
          ...location,
          hasRelation: hasShipToRelationship(this.employeeRelationships(), location.id),
        })),
      );
    });
  }

  private upsertLocations(locationIds: number[], hasCustomerRelation = this.hasCustomerRelation()) {
    const selectedCustomer = this.selectedCustomer();
    if (!this.employeeId || !selectedCustomer || this.saving()) {
      return;
    }

    this.saving.set(true);
    this.employeeService
      .upsertLocationRelationships(
        this.employeeId,
        selectedCustomer.id,
        hasCustomerRelation,
        locationIds,
      )
      .pipe(
        tap((employee) => {
          this.employeeRelationships.set(employee.relationships ?? []);
          this.hasCustomerRelation.set(
            hasCustomerRelationship(employee.relationships ?? [], selectedCustomer.id),
          );
          this.locations.set(
            this.locations()?.map((location) => ({
              ...location,
              hasRelation: locationIds.includes(location.id),
            })),
          );
          this.employeeService.notifyEmployeeChanged(employee.employeeId);
        }),
        catchError((error: HttpErrorResponse) => {
          this.store.dispatch(
            NotificationActions.errorSnackbar({
              message: error.error?.detail ?? extractValidationErrors(error),
            }),
          );
          return of(null);
        }),
        tap(() => {
          this.saving.set(false);
        }),
      )
      .subscribe();
  }
}
